import { useState } from 'react';
import { api } from '../api/client';
import type { UserDetail } from '../api/types';
import { PASSWORD_MIN_LENGTH } from '../utils/password';

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!#$%*+-?';

function generatePassword(length = 16): string {
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => ALPHABET[value % ALPHABET.length]).join('');
}

interface PasswordModalProps {
  user: UserDetail;
  onClose: () => void;
  onSaved: (message: string) => void;
}

export function PasswordModal({ user, onClose, onSaved }: PasswordModalProps) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = password.trim().length >= PASSWORD_MIN_LENGTH && !submitting;

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await api.setPassword(user.id, { password: password.trim() });
      onSaved(`Password changed for ${user.name}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-sm">
        <div className="modal-header">
          <div className="modal-title">Change password</div>
          <div className="modal-subtitle">
            Sets the Keycloak password of <strong>{user.name}</strong> directly.{' '}
            <strong>No email is sent</strong> — pass it on yourself.
          </div>
        </div>
        <div className="modal-body">
          <label className="form-field form-field-wide">
            <span className="form-label">
              New password <span className="form-req">*</span>
            </span>
            <input
              className="form-input mono"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
              autoFocus
            />
          </label>
          <button className="link-button" onClick={() => setPassword(generatePassword())}>
            Generate a strong password
          </button>
          <div className="muted-note password-note">
            The password works immediately and permanently — the user is not asked to
            change it at the next sign-in, and a pending “Update password” action from an
            earlier reset is cleared.
          </div>
          {error && <div className="modal-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={!canSubmit} onClick={() => void submit()}>
            {submitting ? 'Saving…' : 'Change password'}
          </button>
        </div>
      </div>
    </div>
  );
}
